import React, { useState } from 'react';
import { CheckSquare, Plus, Trash2 } from 'lucide-react';
import { ChecklistItem } from '../../types';

interface TechnicalChecklistSectionProps {
  checklists: ChecklistItem[];
  setChecklists: React.Dispatch<React.SetStateAction<ChecklistItem[]>>;
}

export default function TechnicalChecklistSection({
  checklists,
  setChecklists
}: TechnicalChecklistSectionProps) {
  const [newChecklistLabel, setNewChecklistLabel] = useState('');

  const handleToggleCheck = (id: string) => {
    setChecklists(prev => prev.map(c => c.id === id ? { ...c, checked: !c.checked } : c));
  };

  const handleAddCustomChecklist = () => {
    if (!newChecklistLabel.trim()) return;
    const newItem: ChecklistItem = {
      id: `check_${Date.now()}`,
      label: newChecklistLabel.trim(),
      checked: false
    };
    setChecklists(prev => [...prev, newItem]);
    setNewChecklistLabel('');
  };

  const handleRemoveChecklist = (id: string) => {
    setChecklists(prev => prev.filter(c => c.id !== id));
  };

  return (
    <div className="bg-white border border-zinc-100 rounded-3xl p-5 sm:p-6 space-y-4 shadow-sm text-xs">
      <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
        <h3 className="text-xs font-black text-zinc-900 uppercase tracking-wider flex items-center gap-2">
          <CheckSquare className="h-4 w-4 text-brand" />
          <span>Lista de Verificação do Serviço ({checklists.filter(c => c.checked).length}/{checklists.length})</span>
        </h3>
        <span className="text-[10px] font-bold text-zinc-400">Marque os itens verificados</span>
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        {checklists.map(item => (
          <div
            key={item.id}
            onClick={() => handleToggleCheck(item.id)}
            className={`p-3 rounded-2xl border transition-all flex items-center justify-between gap-2 cursor-pointer active:scale-98 ${
              item.checked
                ? 'bg-emerald-50/70 border-emerald-300 text-zinc-900 font-bold'
                : 'bg-zinc-50 border-zinc-100 text-zinc-700 hover:border-zinc-200'
            }`}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <input
                type="checkbox"
                checked={item.checked}
                onChange={() => {}} // Tratado no onClick do container
                className="rounded text-brand focus:ring-brand w-4 h-4 cursor-pointer shrink-0"
              />
              <span className="truncate text-xs">{item.label}</span>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveChecklist(item.id);
              }}
              className="text-zinc-300 hover:text-rose-600 p-1 cursor-pointer"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      <div className="pt-2 flex items-center gap-2">
        <input
          type="text"
          placeholder="Adicionar novo item à lista de verificação..."
          value={newChecklistLabel}
          onChange={e => setNewChecklistLabel(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAddCustomChecklist()}
          className="flex-1 bg-zinc-50 border border-zinc-100 rounded-xl px-3.5 py-2.5 text-xs text-zinc-900 font-medium placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-brand/20"
        />
        <button
          type="button"
          onClick={handleAddCustomChecklist}
          className="px-4 py-2.5 bg-brand hover:bg-brand-dark text-white font-bold text-xs rounded-xl flex items-center gap-1 cursor-pointer transition-all shadow-sm shadow-brand/20 active:scale-95"
        >
          <Plus className="h-3.5 w-3.5" />
          <span>Adicionar</span>
        </button>
      </div>
    </div>
  );
}
